import * as SVG from 'svg.js';
import consts from './consts';
import { translateToSVG,translateFromCanvas} from './shared';
import { Geometry } from './canvasModel';
import { getCVATStore } from '../../../cvat-ui/src/cvat-store';
import { removeObjectAsync, resetCanvas, updateEditedStateAsync, updateAnnotationsAsync} from 'actions/annotation-actions';
import { DrawData} from './canvasModel';

export interface EraserSelector {
    activate(enabled: boolean): void;
    cancel(): void;
    transform(geometry: Geometry): void;
}

export class EraserSelectorImpl implements EraserSelector {
    private onDrawDone: (
        data: any | null,
        duration: number,
        continueDraw?: boolean,
        prevDrawData?: DrawData
    ) => void; // Declare the onDrawDone property

    private geometry: Geometry;
    private getOffset: () => number; // Function to dynamically retrieve offset
    private canvas: SVG.Container;
    private getObjects: () => any[]; // Function to dynamically retrieve objects
    private getSvgShapes: () => any[]; 
    private selectionPolygon: SVG.Polygon | null; // The polygon representing the eraser's selection area
    private polygonPoints: [number, number][]; // Track points of the polygon

    /**
     * Extracts all polygons from the dynamically retrieved objects.
     * @returns An array of SVG.Polygon elements.
     */
    private getPolygons(): SVG.Polygon[] {
        const objects = this.getObjects(); // Dynamically retrieve objects
        const svgShapes = this.getSvgShapes(); // Dynamically retrieve svgShapes
        console.log('Objects in getPolygons:', objects);
    
        return objects
            .filter((obj) => obj.shapeType === 'polygon') // Only handle polygons
            .map((obj) => svgShapes[obj.clientID]) // Match objects to their svgShapes
            .filter((polygon) => !!polygon) as SVG.Polygon[]; // Exclude undefined shapes
    }


    /**
     * Handles mouse movement to update the size and position of the selection rectangle dynamically.
     * @param event - The current mouse event.
     */
    private onMouseMove = (event: MouseEvent): void => {
        if (this.selectionPolygon) {
            const point = translateToSVG((this.canvas.node as any) as SVGSVGElement, [event.clientX, event.clientY]);
            // Add the current mouse point to the polygon
            this.polygonPoints.push([point[0], point[1]]);
            this.selectionPolygon.plot(this.polygonPoints);
        }
    };

    /**
     * Starts drawing the selection rectangle when the mouse button is pressed.
     * @param event - The current mouse event.
     */
    private onMouseDown = (event: MouseEvent): void => {
        const point = translateToSVG((this.canvas.node as any) as SVGSVGElement, [event.clientX, event.clientY]);
        this.polygonPoints = [[point[0], point[1]]]; // Reset and start with the first point
    
        if (!this.selectionPolygon) {
            this.selectionPolygon = this.canvas
                .polygon([])
                .attr({
                    stroke: 'blue', // Blue border
                    'stroke-width': consts.BASE_STROKE_WIDTH / this.geometry.scale,
                    fill: '#D3D3D3', // Light gray fill
                    opacity: 0.3, // 30% opacity
                })
                .addClass('cvat_canvas_shape_eraser_selection');
        }
    };

    /**
     * Finalizes the selection rectangle and applies the eraser operation when the mouse button is released.
     */
    private onMouseUp = (): void => {
        if (this.selectionPolygon && this.polygonPoints.length > 2) {
            this.erasePoints(this.polygonPoints);
            this.selectionPolygon.remove();
            this.selectionPolygon = null;
            this.polygonPoints = [];
        }
    };

    /**
     * Removes points of polygons within the selection rectangle and sends update notifications.
     * @param selectionBox - The coordinates of the selection rectangle.
     */
    private erasePoints(selectionPolygonPoints: [number, number][]): void {
        console.log('Erasing points within the selection polygon:', selectionPolygonPoints);
        const store = getCVATStore();
        
        // Extract polygons
        const polygons = this.getPolygons();
        console.log('Polygons:', polygons);
        
        polygons.forEach((polygon) => {
            const points = polygon.array().value as [number, number][];
            const filteredPoints = points.filter(([px, py]) => {
                return !this.isPointInPolygon([px, py], selectionPolygonPoints);
            });
            
            const clientID = polygon.attr('clientID');
            const object = this.getObjects().find((obj) => obj.clientID === clientID);
            if (!object || object.hidden) {
            console.log(`Skipping hidden or missing object with clientID ${clientID}`);
            return; // skip if object is hidden or missing
        }
    
            // Check if the number of points has changed
            if (filteredPoints.length === points.length) {
                console.log(`Polygon with clientID ${clientID} remains unchanged.`);
                return; // Skip polygons that are unchanged
            }
    
            if (filteredPoints.length >= 3) {
                // Translate points to the required coordinate space
                console.log('Offset:', this.getOffset());
                const flatFilteredPoints: number[] = filteredPoints.flat();
                const translatedPoints = translateFromCanvas(this.getOffset(), flatFilteredPoints);
    
                // Create a new object with the filtered points
                const newObjectData = {
                    points: translatedPoints,
                    attributes: object.attributes,
                    label: object.label,
                    shapeType: 'polygon',
                    frame: object.frame,
                    clientID: undefined,
                    hidden: false,
                    objectType: object.objectType || 'shape',
                    occluded: object.occluded || false,
                    outside: object.outside || false,
                    rotation: object.rotation || 0,
                    zOrder: object.zOrder || 0,
                };
    
                // Call onDrawDone to create the new object
                this.onDrawDone(newObjectData, 200, false);
    
                // Remove the original object
                if (object) {
                    console.log('Removing original object:', object);
                    store.dispatch(removeObjectAsync(object, true));
                }
            } else {
                // Remove polygons with insufficient points
                if (object) {
                    console.log('Removing object with insufficient points:', object);
                    store.dispatch(removeObjectAsync(object, true));
                }
            }
        });
    
        console.log('Erase points operation completed.');
    }
    
    private isPointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
        let inside = false;
        const [px, py] = point;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const [xi, yi] = polygon[i];
            const [xj, yj] = polygon[j];
    
            const intersect =
                yi > py !== yj > py &&
                px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
            if (intersect) inside = !inside;
        }
        return inside;
    }

    /**
     * Activates event listeners to enable the eraser functionality.
     */
    public activate(enabled: boolean): void {
        if (enabled) {
            this.startEraser();
        } else {
            this.release();
        }
    }

    private startEraser(): void {
        console.log('Eraser tool activated');
        this.canvas.node.addEventListener('mousemove', this.onMouseMove);
        this.canvas.node.addEventListener('mousedown', this.onMouseDown);
        this.canvas.node.addEventListener('mouseup', this.onMouseUp);
    
        // Prevent the default context menu on right-click
        this.canvas.node.addEventListener('contextmenu', this.preventContextMenu);
    }
    
    private preventContextMenu = (event: MouseEvent): void => {
        event.preventDefault();
    };
    
    private stopEraser(): void {
        console.log('Eraser tool deactivated');
        this.canvas.node.removeEventListener('mousemove', this.onMouseMove);
        this.canvas.node.removeEventListener('mousedown', this.onMouseDown);
        this.canvas.node.removeEventListener('mouseup', this.onMouseUp);
    
        // Remove the context menu prevention
        this.canvas.node.removeEventListener('contextmenu', this.preventContextMenu);
    }

    /**
     * Resets the eraser tool by stopping all listeners.
     */
    private release(): void {
        this.stopEraser();
    }

    /**
     * Initializes the eraser tool.
     * @param getObjects - Function to retrieve objects dynamically.
     * @param canvas - The SVG canvas element.
     * @param geometry - Geometry data for scaling and transformations.
     */
    public constructor(
        onDrawDone: (
            data: any | null,
            duration: number,
            continueDraw?: boolean,
            prevDrawData?: DrawData
        ) => void,
        getObjects: () => any[],
        getSvgShapes: () => Record<number, SVG.Polygon>,
        canvas: SVG.Container,
        geometry: Geometry,
        getOffset: () => number // Function to dynamically retrieve offset
    ) {
        this.onDrawDone = onDrawDone;
        this.getObjects = getObjects;
        this.getSvgShapes = getSvgShapes;
        this.geometry = geometry;
        this.getOffset = getOffset; // Save the function for later use
        this.canvas = canvas;
        this.selectionPolygon = null;
        this.polygonPoints = [];
    }

    /**
     * Cancels the current eraser operation and resets the state.
     */
    public cancel(): void {
        this.release();
    }

    /**
    * Updates the tool's geometry for transformations like zoom or resize.
    * @param geometry - The updated geometry data.
    */
    public transform(geometry: Geometry): void {
        this.geometry = geometry;
    
        // Update stroke width for the selection polygon
        if (this.selectionPolygon) {
            this.selectionPolygon.attr({
                'stroke-width': consts.BASE_STROKE_WIDTH / geometry.scale,
            });
        }
    }
    
}
